import * as THREE from 'three';
import { HeatmapShaderFrag, HeatmapShaderVert } from './Shaders/HeatMapShader';
import { loadObj } from './ObjLoader';
import { ColorsArray } from './Gradients';
import { clamp } from 'three/src/math/MathUtils';

interface HeatPoint {
    x: number;
    y: number;
    intensity: number;
    radius: number;
    originalX?: number;
    originalY?: number;
}

class FactoryFloorHeatMap {
    private MAX_HEAT_POINTS = 300;
    private MAX_COLORS = 100;
    private object3D: THREE.Object3D | undefined;
    private heatPointsRef: HeatPoint[];
    private activeTransitions: Map<number, { cancel: boolean }> = new Map();
    private uniforms = {
        colors: { value: ColorsArray.map(c => new THREE.Color(c)).concat(Array(this.MAX_COLORS - ColorsArray.length).fill(new THREE.Color(0))) },
        numColors: { value: ColorsArray.length },
        planeSize: { value: new THREE.Vector2(5, 5) },
        heatPoints: { value: Array(this.MAX_HEAT_POINTS).fill(null).map(() => new THREE.Vector2(0, 0)) },
        intensities: { value: new Array(this.MAX_HEAT_POINTS).fill(0.0) },
        radii: { value: new Array(this.MAX_HEAT_POINTS).fill(0.0) },
        radius: { value: 0.5 },
        numPoints: { value: 0 }
    };
    private plane: THREE.Mesh | undefined;
    private floorPath = '/mesh/floor.obj';
    private heatPointsData: { x: number, y: number, intensity: number, radius: number }[] = [];
    private originalHeatmapMaterial: THREE.Material | undefined;
    private basicMaterial: THREE.MeshBasicMaterial | undefined;
    private imageTexture: THREE.Texture | undefined;


    constructor() {
        this.heatPointsRef = [];
        this.setupPlane();
        this.loadImageTexture();
    }

    private loadImageTexture() {
        const textureLoader = new THREE.TextureLoader();
        textureLoader.load(
            '/outerwalls/HowRU MIA-lighter.jpg',
            (texture) => {
                this.imageTexture = texture;
                this.imageTexture.wrapS = THREE.ClampToEdgeWrapping;
                this.imageTexture.wrapT = THREE.ClampToEdgeWrapping;
                this.imageTexture.needsUpdate = true;
            },
            undefined,
            (error) => {
                console.error('An error occurred loading the image texture:', error);
            }
        );
    }

    private setupPlane() {
        const heatmapMaterial = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: HeatmapShaderVert(),
            fragmentShader: HeatmapShaderFrag(),
            side: THREE.DoubleSide
        });
        this.originalHeatmapMaterial = heatmapMaterial;

        loadObj(this.floorPath, (obj) => {
            this.plane = obj.children[0] as THREE.Mesh;
            if (!this.plane) {
                console.error('Plane not loaded');
                return
            }
            this.plane.traverse((child) => {
                if ((child as THREE.Mesh).isMesh) {
                    (child as THREE.Mesh).material = heatmapMaterial;
                }
            });
            this.plane.userData.isCollider = true;
            this.object3D = new THREE.Object3D();
            this.object3D.position.set(0, 0.01, 0);
            this.object3D.add(this.plane);
            this.object3D.castShadow = true;
            this.object3D.receiveShadow = true;
        });
    }

    public clearHeatPoints() {
        this.heatPointsRef = [];
        this.heatPointsData = [];
        this.updateHeatmap(this.heatPointsRef);
    }

    public updateHeatPointsWithTransition(newPoints: HeatPoint[]) {

        const updatedIndices = new Set<number>();

        for (const newPoint of newPoints) {
            const existingIndex = this.heatPointsRef.findIndex(existingPoint =>
                existingPoint.originalX !== undefined && existingPoint.originalY !== undefined &&
                Math.abs(existingPoint.originalX - newPoint.x) < 0.001 &&
                Math.abs(existingPoint.originalY - newPoint.y) < 0.001
            );

            if (existingIndex !== -1) {
                const existingPoint = this.heatPointsRef[existingIndex];
                const updatedPoint = {
                    ...existingPoint,
                    intensity: newPoint.intensity,
                    radius: newPoint.radius
                };
                this.heatPointsRef[existingIndex] = updatedPoint;
                this.heatPointsData[existingIndex] = updatedPoint;
                this.smoothTransition(existingIndex, updatedPoint);
                updatedIndices.add(existingIndex);
            }
        }

        for (const newPoint of newPoints) {
            const hasExisting = this.heatPointsRef.some(existingPoint =>
                existingPoint.originalX !== undefined && existingPoint.originalY !== undefined &&
                Math.abs(existingPoint.originalX - newPoint.x) < 0.001 &&
                Math.abs(existingPoint.originalY - newPoint.y) < 0.001
            );

            if (!hasExisting) {
                this.addHeatPointWithOriginalCoords(newPoint);
            }
        }

        const pointsToRemove: number[] = [];
        this.heatPointsRef.forEach((existingPoint, index) => {
            if (updatedIndices.has(index)) return;

            const stillExists = newPoints.some(newPoint =>
                existingPoint.originalX !== undefined && existingPoint.originalY !== undefined &&
                Math.abs(newPoint.x - existingPoint.originalX) < 0.001 &&
                Math.abs(newPoint.y - existingPoint.originalY) < 0.001
            );
            if (!stillExists) {
                pointsToRemove.push(index);
            }
        });

        pointsToRemove.reverse().forEach(index => {
            if (this.activeTransitions.has(index)) {
                this.activeTransitions.get(index)!.cancel = true;
                this.activeTransitions.delete(index);
            }
            this.heatPointsRef.splice(index, 1);
            this.heatPointsData.splice(index, 1);
        });

        this.uniforms.numPoints.value = Math.min(this.heatPointsRef.length, this.MAX_HEAT_POINTS);
    }

    private addHeatPointWithOriginalCoords(point: HeatPoint) {
        if (this.heatPointsRef.length >= this.MAX_HEAT_POINTS) {
            console.error('Max heat points reached');
            return;
        }

        if (!this.plane) {
            console.error('Plane not set up');
            return;
        }

        const originalX = point.x;
        const originalY = point.y;

        const raycaster = new THREE.Raycaster();
        raycaster.set(new THREE.Vector3(point.x, -5, point.y), new THREE.Vector3(0, 1, 0));

        const intersects = raycaster.intersectObject(this.plane, true);
        if (intersects.length === 0) {
            console.error('No intersection found');
            return;
        }
        const uv = intersects[0].uv;
        if (!uv) return;

        const planeSize = this.uniforms.planeSize.value;
        const shaderX = (uv.x - 0.5) * planeSize.x;
        const shaderY = (uv.y - 0.5) * planeSize.y;

        const heatPoint: HeatPoint = {
            x: shaderX,
            y: shaderY,
            intensity: point.intensity,
            radius: point.radius,
            originalX: originalX,
            originalY: originalY
        };

        this.heatPointsRef.push(heatPoint);
        this.heatPointsData.push(heatPoint);
        const index = this.heatPointsRef.length - 1;
        const numPoints = Math.min(this.heatPointsRef.length, this.MAX_HEAT_POINTS);
        this.uniforms.numPoints.value = numPoints;
        this.uniforms.heatPoints.value[index].set(heatPoint.x, heatPoint.y);
        this.uniforms.intensities.value[index] = 0;
        this.smoothTransition(index, heatPoint);
    }

    public setBasicMaterial(): void {
        if (this.plane) {
            if (this.imageTexture) {
                if (!this.basicMaterial || this.basicMaterial.map !== this.imageTexture) {
                    this.basicMaterial = new THREE.MeshBasicMaterial({ map: this.imageTexture, side: THREE.DoubleSide });
                }
            } else {
                const grayColor = new THREE.Color(0xd3d3d3);
                if (!this.basicMaterial || !this.basicMaterial.color.equals(grayColor) || this.basicMaterial.map) {
                    this.basicMaterial = new THREE.MeshBasicMaterial({ color: grayColor, side: THREE.DoubleSide });
                }
            }
            if (this.basicMaterial) {
                this.plane.material = this.basicMaterial;
            }
        }
    }

    public restoreHeatmapMaterial(): void {
        if (this.plane && this.originalHeatmapMaterial) {
            this.plane.material = this.originalHeatmapMaterial;
        }
    }

    public addHeatPoint(point: HeatPoint) {
        if (this.heatPointsRef.length >= this.MAX_HEAT_POINTS) {
            console.error('Max heat points reached');
            return;
        }

        if (!this.plane) {
            console.error('Plane not set up');
            return;
        }

        // Store original coordinates
        const originalX = point.x;
        const originalY = point.y;

        const raycaster = new THREE.Raycaster();
        raycaster.set(new THREE.Vector3(point.x, -5, point.y), new THREE.Vector3(0, 1, 0));

        const intersects = raycaster.intersectObject(this.plane, true);
        if (intersects.length === 0) {
            console.error('No intersection found');
            return;
        }
        const uv = intersects[0].uv;
        if (!uv) return;
        const planeSize = this.uniforms.planeSize.value;
        const shaderX = (uv.x - 0.5) * planeSize.x;
        const shaderY = (uv.y - 0.5) * planeSize.y;

        point.x = shaderX;
        point.y = shaderY;
        point.originalX = originalX;
        point.originalY = originalY;

        const existingPointIndex = this.heatPointsRef.findIndex(p =>
            p.originalX !== undefined && p.originalY !== undefined &&
            Math.abs(p.originalX - originalX) < 0.001 && Math.abs(p.originalY - originalY) < 0.001
        );
        if (existingPointIndex !== -1) {
            this.heatPointsRef[existingPointIndex] = point;
            this.heatPointsData[existingPointIndex] = point;
            this.smoothTransition(existingPointIndex, point);
            return;
        }

        this.heatPointsRef.push(point);
        this.heatPointsData.push(point);
        const index = this.heatPointsRef.length - 1;
        const numPoints = Math.min(this.heatPointsRef.length, this.MAX_HEAT_POINTS);
        this.uniforms.numPoints.value = numPoints;
        this.uniforms.heatPoints.value[index].set(point.x, point.y);
        this.uniforms.intensities.value[index] = 0;
        this.smoothTransition(index, point);
    }

    public updateHeatPointDirect(point: HeatPoint) {
        const existingPointIndex = this.heatPointsRef.findIndex(p =>
            Math.abs(p.x - point.x) < 0.001 && Math.abs(p.y - point.y) < 0.001
        );

        if (existingPointIndex !== -1) {
            this.heatPointsRef[existingPointIndex] = point;
            this.heatPointsData[existingPointIndex] = point;
            this.smoothTransition(existingPointIndex, point);
            return;
        }

        if (this.heatPointsRef.length >= this.MAX_HEAT_POINTS) {
            console.error('Max heat points reached');
            return;
        }

        this.heatPointsRef.push(point);
        this.heatPointsData.push(point);
        const index = this.heatPointsRef.length - 1;
        const numPoints = Math.min(this.heatPointsRef.length, this.MAX_HEAT_POINTS);
        this.uniforms.numPoints.value = numPoints;
        this.uniforms.heatPoints.value[index].set(point.x, point.y);
        this.uniforms.intensities.value[index] = 0;
        this.smoothTransition(index, point);
    }

    private smoothTransition(pointIndex: number, point: HeatPoint) {
        if (this.activeTransitions.has(pointIndex)) {
            this.activeTransitions.get(pointIndex)!.cancel = true;
        }

        const transitionControl = { cancel: false };
        this.activeTransitions.set(pointIndex, transitionControl);

        const oldPointIntensity = this.uniforms.intensities.value[pointIndex];
        const targetIntensity = point.intensity;
        const duration = 500;
        const startTime = performance.now();

        const animate = (currentTime: number) => {
            if (transitionControl.cancel) {
                this.activeTransitions.delete(pointIndex);
                return;
            }

            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeProgress = progress < 0.5
                ? 2 * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;

            this.uniforms.intensities.value[pointIndex] =
                oldPointIntensity + (targetIntensity - oldPointIntensity) * easeProgress;

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                this.uniforms.intensities.value[pointIndex] = targetIntensity;
                this.activeTransitions.delete(pointIndex);
            }
        };

        requestAnimationFrame(animate);
    }


    private updateHeatmap(points: HeatPoint[]) {
        const numPoints = Math.min(points.length, this.MAX_HEAT_POINTS);
        this.uniforms.numPoints.value = numPoints;

        for (let i = 0; i < numPoints; i++) {
            this.uniforms.heatPoints.value[i].set(points[i].x, points[i].y);
            this.uniforms.intensities.value[i] = points[i].intensity;
        }
    }

    public getObject3D() {
        if (!this.object3D) {
            console.error('Object3D not set up');
            return undefined;
        }
        return this.object3D;
    }

    public setRadius(radius: number) {
        radius = clamp(radius, 0.2, 0.5);
        this.uniforms.radius.value = radius;
    }
}

export default FactoryFloorHeatMap;